import React, { Component } from 'react';
import Table from '../../Base/Table';
import { DEFAULT_PER_PAGE } from 'common/oldJavascripts/config.js';
import LayoutContent from 'common/oldJavascripts/components/Base/Layout/LayoutContent';
import Box from '../../Base/Box';
import Header from '../../Base/Header';
import Blankslate from '../../Base/Blankslate';
import Pagination from '../../Base/Pagination';
import Loader from '../../Base/Loader';
import Search from '../../Shared/Search';
import BaseLayout from '../Layouts/Base';
import T from '../../../utils/i18n';

import * as Sentry from '@sentry/browser';

// HoC
import withApi from 'common/hoc/withApi';

import BoxItem from 'common/oldJavascripts/components/Base/Box/BoxItem';

var Row = Table.Row;
var Cell = Table.Cell;

const PRIVILEGES_TO_MAP = [
  {
    privilege: 'can_create_offers',
    route: id => `/projects/${id}/offers`,
  },
  {
    privilege: 'can_review_offers',
    route: id => `/projects/${id}/reviews`,
  },
  {
    privilege: 'can_review_offers',
    route: id => `/projects/${id}/reviews`,
  },
  {
    privilege: 'can_approve_offers',
    route: id => `/projects/${id}/approvals`,
  },
  {
    privilege: 'can_i9_verify_offers',
    route: id => `/projects/${id}/i9-verifications`,
  },
  {
    privilege: 'can_be_i9_verifier',
    route: id => `/projects/${id}/i9-verifications`,
  },
  {
    privilege: 'can_view_documents',
    route: id => `/projects/${id}/offer-documents`,
  },
  {
    privilege: 'can_access_archives',
    route: id => `${id}/archives`,
  },
];

class ProjectList extends Component {
  static queries = {
    projects: {
      info: function(params, related) {
        var query = related['/router/query'];

        return {
          id: '/projects',

          cursor: {
            page: query.page,
            per_page: query.per_page || DEFAULT_PER_PAGE,
            q: query.q,
          },
        };
      },
    },

    me: {
      info: function() {
        return { id: '/me' };
      },
    },
  };

  render() {
    const { projects: projectsQuery = {}, me: meQuery = {} } = this.props;
    const {
      status: projectsStatus,
      data: {
        items: projects = [],
        total_pages: projectsTotalPages,
        page: projectsCurrentPage,
      } = {},
    } = projectsQuery || {};
    const {
      status: meQuerysStatus,
      data: { is_admin: isAdmin, is_support_admin: isSupportAdmin },
    } = meQuery || {};

    const isLoading =
      projectsStatus === 'loading' || meQuerysStatus === 'loading';
    const projectsNotEmpty = projects.length > 0;

    const renderLoading = () => (
      <Row>
        <Cell noBorder={true} />
        <Cell noBorder={true}>
          <Loader />
        </Cell>
        <Cell noBorder={true} />
      </Row>
    );

    const renderProjectRow = (project = {}) => {
      const { privileges, can_continue: projectIsAccessible } = project || {};
      // Multiple users get access to the project list
      // Depending on their role and permissions they go to different locations when they click
      let to;
      if (projectIsAccessible === false) {
        // This project is for 1.0 and so the user should go to the change app screen
        to = `/projects/${project.id}/change-app`;
      } else if (isAdmin) {
        // This user is an admin and will go to the project setup
        to = `/projects/${project.id}/settings`;
      } else if (isSupportAdmin) {
        // This user probably doesnt exist because we dont use support admin
        // But they go to manage crew members
        to = `/projects/${project.id}/crew`;
      } else if (privileges) {
        // This user is a studio admin so we figure out where they should go
        // based of the privileges in the projects query and the Privileges to map array
        const firstMatchedPrivilege = PRIVILEGES_TO_MAP.find(({ privilege }) =>
          privileges.includes(privilege),
        );
        to = firstMatchedPrivilege.route(project.id);
      } else {
        // If a user arrived here then there is a problem with this users permission configurations
        // Or they have been revoked access
        to = '/projects';
        Sentry.captureMessage(
          'Can not find appropriate route for user from project list',
          {
            extra: {
              meQuery,
              projectsQuery,
            },
          },
        );
      }
      return (
        <Row
          to={to}
          params={{ projectId: project.id }}
          data-test-id={`Projects-row-${project.id}`}
        >
          <Cell rowHeader={true} doubleBorderBottom={true}>
            <span data-test-id={`Projects-name-${project.id}`}>
              {project.name}
            </span>
          </Cell>
          <Cell doubleBorderBottom={true}>
            <span data-test-id={`Projects-productionCompany-${project.id}`}>
              {project.production_company_name}
            </span>
          </Cell>
          <Cell doubleBorderBottom={true}>
            <span data-test-id={`Projects-producer-${project.id}`}>
              {project.producer_name}
            </span>
          </Cell>
        </Row>
      );
    };

    return (
      <BaseLayout>
        <LayoutContent>
          <Box>
            <BoxItem>
              <Header>
                <Header.Title>
                  <span data-test-id="Projects-title">
                    {T('projects.title')}
                  </span>
                </Header.Title>

                <Header.Nav>
                  <Search />
                </Header.Nav>
              </Header>
            </BoxItem>
            <Table>
              <Table.Header>
                <Cell header={true}>{T('projects.columns.name')}</Cell>
                <Cell header={true}>
                  {T('projects.columns.production_company')}
                </Cell>
                <Cell header={true}>{T('projects.columns.producer')}</Cell>
              </Table.Header>
              {isLoading && renderLoading()}
              {!isLoading && projectsNotEmpty && projects.map(renderProjectRow)}
              {!isLoading && !projectsNotEmpty && (
                <Blankslate>{T('projects.not_found')}</Blankslate>
              )}
            </Table>
          </Box>
          {!isLoading && projectsNotEmpty && (
            <Pagination
              totalPages={projectsTotalPages}
              page={projectsCurrentPage}
            />
          )}
        </LayoutContent>
      </BaseLayout>
    );
  }
}

export default withApi(ProjectList);
